import { useInfiniteQuery } from "@tanstack/react-query";
import { FC, memo, useMemo } from "react";

import { SrOnly } from "@/components/Layout/Parts";
import ScrollableTable from "@/components/ScrollableTable";
import { SortableTh, TableContent, TableHeader, Th } from "@/components/Table";
import useSort from "@/hooks/useSort";
import {
  OptimisationProjectResource,
  PaginatedResponse,
} from "@/types/resources";
import { api } from "@/utils/api";
import {
  OptimisationProjectNetworkEnum,
  OptimisationProjectStatusEnum,
  OptimisationProjectTypeEnum,
  SortDirection,
} from "@/utils/enums";
import { StyledSectionHeading, StyledTable } from "./OptimisationTable";
import OptimisationTableRow from "./OptimisationTableRow";
import InfiniteLoader from "@/components/InfiniteLoader";

interface Props {
  teamId?: string;
  network: OptimisationProjectNetworkEnum;
}

const EndedProjects: FC<Props> = memo(({ teamId, network }) => {
  const [sort, setSort] = useSort({
    direction: SortDirection.DEFAULT,
    property: "projectId",
  });

  const {
    data,
    isError,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery<PaginatedResponse<OptimisationProjectResource>>(
    [
      "optimisation_tests",
      {
        teamId,
        status: OptimisationProjectStatusEnum.ENDED,
        sort,
      },
    ],
    ({ pageParam = `/v1/optimisation-tests` }) =>
      api.get<never, PaginatedResponse<OptimisationProjectResource>>(
        pageParam,
        {
          params: {
            per_page: 20,
            "filter[team_id]": teamId,
            "filter[type]": OptimisationProjectTypeEnum.OWN,
            "filter[network]": network,
            "filter[status]": OptimisationProjectStatusEnum.ENDED,
            ...(sort.direction !== SortDirection.DEFAULT && {
              sort: `${
                sort.direction === SortDirection.ASC ? "" : "-"
              }projectId`,
            }),
          },
        }
      ),
    {
      getNextPageParam: (lastPage) => {
        return lastPage.links.next;
      },
      staleTime: 60 * 1000,
    }
  );

  const projects = useMemo(
    () =>
      Object.keys(data?.pages || {}).reduce<OptimisationProjectResource[]>(
        (acc, currKey: any) => {
          return [...acc, ...data!.pages[currKey].data];
        },
        []
      ),
    [data]
  );

  return (
    <>
      <TableHeader>
        <StyledSectionHeading>ENDED</StyledSectionHeading>
      </TableHeader>
      <ScrollableTable>
        <StyledTable $fixed>
          <thead>
            <tr>
              <SortableTh
                sort={sort}
                setSort={setSort}
                title="Project Name"
                subtitle="Project ID"
                property="projectId"
              />
              <Th>Thumbs</Th>
              <Th>Tips</Th>
              <Th>Sequences</Th>
              <Th>Intros</Th>
              <Th $actionsCol>
                <SrOnly>Actions</SrOnly>
              </Th>
            </tr>
          </thead>
          <tbody>
            <TableContent
              isLoading={isLoading}
              isError={isError}
              hasData={!!projects?.length}
            >
              {projects?.map((project) => (
                <OptimisationTableRow project={project} key={project.id} />
              ))}
            </TableContent>
          </tbody>
        </StyledTable>
      </ScrollableTable>
      <InfiniteLoader
        hasNextPage={hasNextPage}
        fetchNextPage={fetchNextPage}
        isFetchingNextPage={isFetchingNextPage}
      />
    </>
  );
});

export default EndedProjects;
